import { useEffect, useState } from "react";

import { DateTime } from "luxon";
import {
  ArrowPathIcon,
  CheckIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import Modal from "../../layouts/Modal";
import classNames from "../../utils/classNames";
import { IVehicle } from "../../models/Vehicle";
import useVehiclesMutations from "../../data/useVehiclesMutations";

type VehicleCompareField = {
  label: string;
  format: (v: IVehicle) => string | null;
};

const VEHICLE_COMPARE_FIELDS: VehicleCompareField[] = [
  { label: "Reg", format: (v) => v.vrm || null },
  { label: "VIN", format: (v) => v.combinedVIN || v.vinOriginalDVLA || null },
  { label: "Year", format: (v) => v.dvlaYearOfManufacture?.toString() ?? null },
  { label: "Make", format: (v) => v.combinedMake || v.dvlaMake || null },
  { label: "Model", format: (v) => v.combinedModel || v.dvlaModel || null },
  { label: "Trim", format: (v) => v.modelVariantDescription || null },
  { label: "Series", format: (v) => v.modelSeries || null },
  { label: "Body Style", format: (v) => v.bodyStyleDescription || v.bodyStyle || null },
  { label: "Doors", format: (v) => v.numberOfDoors?.toString() ?? null },
  { label: "Seats", format: (v) => (v.seats ?? v.seatingCapacityInclDriver)?.toString() ?? null },
  {
    label: "Mileage",
    format: (v) =>
      typeof v.odometerInMiles === "number"
        ? `${v.odometerInMiles.toLocaleString("en-GB")} mi`
        : null,
  },
  { label: "Fuel Type", format: (v) => v.combinedFuelType || v.fuelType || v.fuelDescription || null },
  { label: "Engine Code", format: (v) => v.engineModelCode || null },
  {
    label: "Engine Size",
    format: (v) =>
      typeof v.combinedEngineCapacity === "number"
        ? `${v.combinedEngineCapacity}cc`
        : typeof v.engineCapacity === "number"
        ? `${v.engineCapacity}cc`
        : null,
  },
  { label: "Cylinders", format: (v) => v.numberOfCylinders?.toString() ?? null },
  { label: "Aspiration", format: (v) => v.aspiration || null },
  { label: "Transmission", format: (v) => v.combinedTransmission || v.dvlaTransmission || v.transmission || null },
  { label: "Forward Gears", format: (v) => (v.combinedForwardGears ?? v.forwardGears ?? v.dvlaForwardGears)?.toString() ?? null },
  { label: "Drive Type", format: (v) => v.driveType || v.driveAxle || null },
  { label: "BHP", format: (v) => (typeof v.maximumPowerBHP === "number" ? `${v.maximumPowerBHP} bhp` : null) },
  { label: "Torque", format: (v) => (typeof v.maximumTorqueLbFt === "number" ? `${v.maximumTorqueLbFt} lb·ft` : null) },
  { label: "0-100 km/h", format: (v) => (typeof v.accelerationTo100KPHSecs === "number" ? `${v.accelerationTo100KPHSecs}s` : null) },
  { label: "Top Speed", format: (v) => (typeof v.maximumSpeedMPH === "number" ? `${v.maximumSpeedMPH} mph` : null) },
  { label: "CO₂", format: (v) => (typeof v.cO2 === "number" ? `${v.cO2} g/km` : null) },
  { label: "Euro Status", format: (v) => v.euroStatus || null },
  { label: "Drive Side", format: (v) => v.lrHandDrive || null },
  { label: "Country", format: (v) => v.countryOfOrigin || null },
  { label: "Colour", format: (v) => v.colourCurrent || v.colourOriginal || null },
  { label: "Previous Keepers", format: (v) => v.numberOfPreviousKeepers?.toString() ?? null },
  { label: "First Registered", format: (v) => (v.dateFirstRegistered ? DateTime.fromISO(v.dateFirstRegistered).toFormat("dd LLL yyyy") : null) },
  { label: "Manufacture Date", format: (v) => (v.manufacturerBuildDate ? DateTime.fromISO(v.manufacturerBuildDate).toFormat("dd LLL yyyy") : null) },
];

interface VehicleCompareModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  donor: IVehicle;
  initialVrm?: string;
  /**
   * Called when a comparison VRM is successfully looked up, so callers can
   * remember it across multiple opens of the modal (e.g. the basket page
   * persists the last reg the user entered).
   */
  onVrmChange?: (vrm: string) => void;
}

export default function VehicleCompareModal({
  open,
  setOpen,
  donor,
  initialVrm = "",
  onVrmChange,
}: VehicleCompareModalProps) {
  const { vrmLookup } = useVehiclesMutations();
  const [compareVrm, setCompareVrm] = useState("");
  const [compareVehicle, setCompareVehicle] = useState<IVehicle | null>(null);
  const [error, setError] = useState<string | null>(null);

  const runLookup = (reg: string) => {
    const trimmed = reg.trim();
    if (!trimmed) return;
    setError(null);
    vrmLookup.mutate(trimmed, {
      onSuccess: (data) => {
        if (data?.data) {
          setCompareVehicle(data.data);
          onVrmChange?.(trimmed.toUpperCase());
        } else {
          setError("No vehicle found for that registration.");
          setCompareVehicle(null);
        }
      },
      onError: () => {
        setError("Lookup failed. Check the registration and try again.");
        setCompareVehicle(null);
      },
    });
  };

  useEffect(() => {
    if (!open) {
      setCompareVrm("");
      setCompareVehicle(null);
      setError(null);
      return;
    }
    const seeded = (initialVrm ?? "").trim().toUpperCase();
    if (seeded && seeded !== (donor.vrm ?? "").trim().toUpperCase()) {
      setCompareVrm(seeded);
      runLookup(seeded);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleLookup = () => runLookup(compareVrm);

  const rows = VEHICLE_COMPARE_FIELDS.map((field) => {
    const donorValue = field.format(donor);
    const compareValue = compareVehicle ? field.format(compareVehicle) : null;
    return { field, donorValue, compareValue };
  });

  return (
    <Modal open={open} setOpen={setOpen} width="max-w-5xl">
      <div onClick={(e) => e.stopPropagation()} className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Compare Vehicles</h2>
            <p className="text-xs text-slate-500">
              Side-by-side UKVD specification comparison.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close"
            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-400"
          >
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Donor Vehicle
            </p>
            <p className="mt-1 text-base font-semibold text-slate-800">
              {donor.combinedMake} {donor.combinedModel}
            </p>
            <p className="text-xs text-slate-500">
              {donor.vrm ?? "No reg"} · {donor.dvlaYearOfManufacture ?? "—"}
            </p>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Compare With
            </p>
            <div className="mt-1 flex items-center gap-2">
              <div
                className={classNames(
                  "flex flex-1 items-stretch overflow-hidden rounded-md border-2 border-black shadow-sm",
                  "focus-within:ring-2 focus-within:ring-primary-500"
                )}
              >
                <span
                  aria-hidden="true"
                  className="flex w-6 items-center justify-center bg-blue-700 text-[10px] font-bold uppercase tracking-widest text-yellow-300"
                >
                  UK
                </span>
                <input
                  type="text"
                  value={compareVrm}
                  onChange={(e) => setCompareVrm(e.target.value.toUpperCase())}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleLookup();
                    }
                  }}
                  placeholder="ENTER REG"
                  aria-label="Comparison vehicle registration"
                  disabled={vrmLookup.isLoading}
                  className={classNames(
                    vrmLookup.isLoading ? "opacity-60" : "",
                    "w-full border-0 bg-yellow-300 px-3 py-1 text-center text-xl font-bold uppercase tracking-[0.25em] text-black placeholder:text-black/40 placeholder:tracking-[0.15em] placeholder:text-sm focus:outline-none focus:ring-0"
                  )}
                  style={{
                    fontFamily:
                      '"UKNumberPlate", "Charles Wright", "Arial Black", "Helvetica Neue", sans-serif',
                  }}
                />
                <button
                  type="button"
                  onClick={handleLookup}
                  disabled={!compareVrm.trim() || vrmLookup.isLoading}
                  title={vrmLookup.isLoading ? "Looking up…" : "Lookup registration"}
                  aria-label="Lookup registration"
                  className={classNames(
                    "flex w-9 items-center justify-center bg-black text-yellow-300 transition-colors",
                    !compareVrm.trim() || vrmLookup.isLoading
                      ? "cursor-not-allowed opacity-60"
                      : "hover:bg-slate-800 cursor-pointer"
                  )}
                >
                  {vrmLookup.isLoading ? (
                    <ArrowPathIcon className="h-5 w-5 animate-spin" aria-hidden="true" />
                  ) : (
                    <MagnifyingGlassIcon className="h-5 w-5" aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>
            {compareVehicle && (
              <p className="mt-1 text-xs text-slate-500">
                {compareVehicle.combinedMake} {compareVehicle.combinedModel} ·{" "}
                {compareVehicle.dvlaYearOfManufacture ?? "—"}
              </p>
            )}
            {error && <p className="mt-1 text-xs text-rose-600">{error}</p>}
          </div>
        </div>

        <div className="max-h-[60vh] overflow-y-auto rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="w-10 px-2 py-2 text-center font-semibold"></th>
                <th className="w-1/4 px-3 py-2 font-semibold">Field</th>
                <th className="px-3 py-2 font-semibold">Donor</th>
                <th className="px-3 py-2 font-semibold">Compare</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map(({ field, donorValue, compareValue }) => {
                const hasBoth = !!donorValue && !!compareValue;
                const matches =
                  hasBoth &&
                  donorValue!.trim().toLowerCase() ===
                    compareValue!.trim().toLowerCase();
                const differs = hasBoth && !matches;

                let indicator: React.ReactNode = (
                  <span className="text-slate-300" aria-hidden="true">
                    —
                  </span>
                );
                let indicatorLabel = "Not enough data to compare";

                if (compareVehicle) {
                  if (matches) {
                    indicator = (
                      <span
                        className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200"
                        aria-hidden="true"
                      >
                        <CheckIcon className="h-3.5 w-3.5" />
                      </span>
                    );
                    indicatorLabel = "Match";
                  } else if (differs) {
                    indicator = (
                      <span
                        className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-rose-100 text-rose-700 ring-1 ring-rose-200"
                        aria-hidden="true"
                      >
                        <XMarkIcon className="h-3.5 w-3.5" />
                      </span>
                    );
                    indicatorLabel = "Different";
                  }
                }

                return (
                  <tr
                    key={field.label}
                    className={classNames(
                      "transition-colors",
                      differs
                        ? "bg-rose-50"
                        : matches
                        ? "bg-emerald-50/40"
                        : "hover:bg-slate-50"
                    )}
                  >
                    <td className="px-2 py-1.5 text-center" title={indicatorLabel}>
                      <span className="sr-only">{indicatorLabel}</span>
                      {indicator}
                    </td>
                    <td className="px-3 py-1.5 text-xs font-medium text-slate-500">
                      {field.label}
                    </td>
                    <td className="px-3 py-1.5 text-slate-800">
                      {donorValue ?? <span className="text-slate-300">—</span>}
                    </td>
                    <td
                      className={classNames(
                        "px-3 py-1.5",
                        differs
                          ? "font-semibold text-rose-800"
                          : matches
                          ? "text-emerald-800"
                          : "text-slate-800"
                      )}
                    >
                      {compareValue ?? (
                        <span className="text-slate-300">
                          {compareVehicle ? "—" : ""}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {compareVehicle && (
          <div className="flex items-center justify-end gap-3 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1">
              <span className="inline-flex h-3 w-3 items-center justify-center rounded-full bg-emerald-100 ring-1 ring-emerald-200">
                <CheckIcon className="h-2.5 w-2.5 text-emerald-700" />
              </span>
              Match
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-flex h-3 w-3 items-center justify-center rounded-full bg-rose-100 ring-1 ring-rose-200">
                <XMarkIcon className="h-2.5 w-2.5 text-rose-700" />
              </span>
              Different
            </span>
          </div>
        )}
      </div>
    </Modal>
  );
}
